import type { SurveyListSelectDto, SurveySelectDto, TechnologySelectDto } from '@@types/api.types';
import { AssessmentSubtype, AssessmentType, SurveyStatus } from '@@types/api.types';
import { DropdownMenuComponent } from '@components/core/dropdown-menu.component';
import { IconButton } from '@components/core/icon-button.component';
import { TooltipComponent } from '@components/core/tooltip.component';
import { ConfirmDeleteModal } from '@components/shared/confirm-delete-modal.component';
import { ProgressBar } from '@components/shared/progressbar.component';
import { Score } from '@components/shared/score.component';
import { Tile } from '@components/shared/tile.component';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import { useSurveyNavigation } from '@hooks/surveys.hook';
import { useInnovatorDeleteSurvey } from '@queries/survey.query';
import { EllipsisVertical } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { BlueLabel } from './blue-label.component';

type SurveyAssessmentKind = 'militaryTrl' | 'mrl' | 'trl';

const ASSESSMENT_TYPE_TITLES: Record<AssessmentType, string> = {
  CYBER: 'Cybersecurity',
  MRL: 'MRL',
  TRL: 'TRL',
};

const ASSESSMENT_TYPE_SCORE_TYPE: Record<AssessmentType, 'MRL' | 'TRL'> = {
  CYBER: 'TRL',
  MRL: 'MRL',
  TRL: 'TRL',
};

const getAssessmentKindLabel = (assessmentKind: SurveyAssessmentKind): string => {
  switch (assessmentKind) {
    case 'militaryTrl':
      return 'Military TRL';
    case 'mrl':
      return 'MRL';
    case 'trl':
      return 'TRL';
    default:
      return 'TRL';
  }
};

const getAssessmentKindFromSurvey = (survey: SurveyListSelectDto | SurveySelectDto): SurveyAssessmentKind => {
  const isMilitaryTrl =
    survey.assessmentType === AssessmentType.TRL && survey.assessmentSubtype === AssessmentSubtype.MILITARY;
  if (isMilitaryTrl) {
    return 'militaryTrl';
  }
  return survey.assessmentType === AssessmentType.MRL ? 'mrl' : 'trl';
};

const getCtaButtonText = (assessmentKind: SurveyAssessmentKind): string => {
  switch (assessmentKind) {
    case 'militaryTrl':
      return 'Start New Military TRL';
    case 'mrl':
      return 'Start New MRL';
    case 'trl':
      return 'Start New TRL';
    default:
      return 'Start New TRL';
  }
};

const getCtaSubtitleText = (params: { assessmentKind: SurveyAssessmentKind; hasExistingScore: boolean }): string => {
  const { assessmentKind, hasExistingScore } = params;
  const assessmentLabel = getAssessmentKindLabel(assessmentKind);
  if (hasExistingScore) {
    return `Revise your ${assessmentLabel} Score`;
  }
  return `Get your first ${assessmentLabel} Score`;
};

type UseDomainInfo = {
  domainNames: string[];
  useName: string;
};

const extractUseDomainInfo = (survey: SurveyListSelectDto | SurveySelectDto): UseDomainInfo[] => {
  const surveyUseDomains = survey.surveyUseDomains ?? [];

  if (surveyUseDomains.length === 0) {
    return [];
  }

  const useMap = new Map<string, { domainNames: string[]; useName: string }>();

  for (const surveyUseDomain of surveyUseDomains) {
    const domainName = surveyUseDomain.domain?.name ?? '';
    const useId = surveyUseDomain.useId;
    const useName = surveyUseDomain.use?.name ?? '';

    if (!useMap.has(useId)) {
      useMap.set(useId, { domainNames: [], useName });
    }

    const useInfo = useMap.get(useId);
    if (useInfo && domainName) {
      useInfo.domainNames.push(domainName);
    }
  }

  return Array.from(useMap.values());
};

type SurveyCTAProps = {
  /**
   * Assessment kind to determine non-draft CTA text.
   */
  assessmentKind?: SurveyAssessmentKind;
  /**
   * Optional CTA button text override.
   */
  ctaButtonText?: string;
  /**
   * Optional CTA subtitle text override.
   */
  ctaSubtitleText?: string;
  /**
   * Optional custom draft card configuration.
   * When provided, the draft UI is rendered from this config instead of survey-specific logic.
   */
  draftCard?: {
    isSelected?: boolean;
    onCardClick?: () => void;
    onDiscardClick?: () => Promise<void> | void;
    onResumeClick: () => void;
    progress: number;
    renderSummary: () => React.ReactNode;
    resumeButtonText?: string;
    showResumeButton?: boolean;
  };
  /**
   * Whether the CTA is disabled (used for Military TRL with incomplete MA data)
   */
  disabled?: boolean;
  /**
   * Reason for disabled state (shown in tooltip)
   */
  disabledReason?: string;
  /**
   * Whether there is already an existing assessment score for this CTA context.
   * Used to switch subtitle between first-time and revise copy.
   */
  hasExistingScore?: boolean;
  /**
   * Whether to hide the subtitle when the CTA is disabled.
   */
  hideSubtitleWhenDisabled?: boolean;
  /**
   * Whether the CTA is in loading state
   */
  loading?: boolean;
  /**
   * Callback when CTA button is clicked (for custom start logic)
   * If not provided, uses default navigation behavior.
   */
  onStartClick?: () => void;
  /**
   * Whether to show the CTA button when no survey exists.
   * Defaults to true. Set to false for home page behavior.
   */
  showCta?: boolean;
  /**
   * Optional survey in progress (draft)
   */
  survey?: SurveyListSelectDto | SurveySelectDto;
  /**
   * Technology data (needed for navigation)
   */
  technology?: TechnologySelectDto;
  /**
   * Optional title override shown above Score in draft state.
   */
  title?: React.ReactNode;
};

export const SurveyCTA: FC<SurveyCTAProps> = (props) => {
  const {
    assessmentKind,
    ctaButtonText,
    ctaSubtitleText,
    disabled = false,
    disabledReason,
    draftCard,
    hasExistingScore = false,
    hideSubtitleWhenDisabled = false,
    loading = false,
    onStartClick,
    showCta = true,
    survey,
    technology,
    title,
  } = props;

  const { navigateToSurvey } = useSurveyNavigation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { mutateAsync: deleteSurvey } = useInnovatorDeleteSurvey();

  const useDomainInfo = useMemo(() => {
    if (!survey) {
      return [];
    }
    return extractUseDomainInfo(survey);
  }, [survey]);

  const resolvedAssessmentKind = useMemo<SurveyAssessmentKind>(() => {
    if (assessmentKind) {
      return assessmentKind;
    }
    if (!survey) {
      return 'trl';
    }
    return getAssessmentKindFromSurvey(survey);
  }, [assessmentKind, survey]);

  const resolvedCtaButtonText = ctaButtonText ?? getCtaButtonText(resolvedAssessmentKind);
  const resolvedCtaSubtitleText =
    ctaSubtitleText ??
    getCtaSubtitleText({
      assessmentKind: resolvedAssessmentKind,
      hasExistingScore: hasExistingScore || (survey?.status !== undefined && survey.status !== SurveyStatus.DRAFT),
    });

  if (!survey && !showCta && !draftCard) {
    return null;
  }

  const handleNavigateToSurvey = (): void => {
    if (!survey) {
      return;
    }

    const { id, technology: surveyTechnology } = survey;
    const technologyId = surveyTechnology?.id ?? technology?.id;

    if (!technologyId) {
      return;
    }

    navigateToSurvey(`/technology/${technologyId}/survey/${id}?viewFirstUnansweredQuestion=true`);
  };

  const handleDelete = (): void => {
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async (): Promise<void> => {
    if (!survey) {
      return;
    }

    await deleteSurvey({ surveyId: survey.id });
  };

  if (draftCard) {
    const {
      isSelected,
      onCardClick,
      onDiscardClick,
      onResumeClick,
      progress,
      renderSummary,
      resumeButtonText,
      showResumeButton = true,
    } = draftCard;

    return (
      <>
        <Tile
          className={isSelected ? 'ring-2 ring-[#3D2EFFCC] ring-inset bg-linear-to-b bg-[#3D2EFF12]' : undefined}
          hoverReveal
        >
          <div className="flex flex-col gap-3">
            <div className="flex justify-between gap-2">
              {onCardClick ? (
                <button
                  className="min-w-0 flex-1 text-left"
                  onClick={onCardClick}
                  type="button"
                >
                  {renderSummary()}
                </button>
              ) : (
                <div className="min-w-0 flex-1 text-left">{renderSummary()}</div>
              )}

              {onDiscardClick && (
                <DropdownMenuComponent
                  items={[
                    {
                      onClick: () => {
                        void onDiscardClick();
                      },
                      text: 'Discard',
                      variant: 'danger',
                    },
                  ]}
                  trigger={
                    <IconButton.HoverReveal
                      ariaLabel="Open menu"
                      icon={<EllipsisVertical />}
                      variant="secondary"
                    />
                  }
                />
              )}
            </div>

            <div className="flex w-full flex-col gap-2">
              <ProgressBar progress={progress} />
              {showResumeButton && (
                <Button
                  className="w-full"
                  onClick={onResumeClick}
                  variant="primary"
                >
                  {resumeButtonText ?? 'Resume'}
                </Button>
              )}
            </div>
          </div>
        </Tile>
      </>
    );
  }

  if (!survey || survey.status !== SurveyStatus.DRAFT) {
    const handlePrimaryClick = (): void => {
      if (onStartClick) {
        onStartClick();
        return;
      }

      handleNavigateToSurvey();
    };

    const ctaButton = (
      <Button
        className="w-full"
        disabled={disabled}
        loading={loading}
        onClick={handlePrimaryClick}
      >
        {resolvedCtaButtonText}
      </Button>
    );

    return (
      <Tile className="p-4">
        <div className="flex flex-col gap-2">
          {title && <div className="text-base font-medium text-gray-600">{title}</div>}
          {disabled && disabledReason ? (
            <TooltipComponent
              followCursor
              side="top"
              trigger={<div className="w-full">{ctaButton}</div>}
            >
              {disabledReason}
            </TooltipComponent>
          ) : (
            ctaButton
          )}
          {!(disabled && hideSubtitleWhenDisabled) && (
            <span className="text-center text-sm font-medium text-[#3D2EFF]">{resolvedCtaSubtitleText}</span>
          )}
        </div>
      </Tile>
    );
  }

  const isMilitaryTrl =
    survey.assessmentType === AssessmentType.TRL && survey.assessmentSubtype === AssessmentSubtype.MILITARY;
  const isTrlBased = survey.assessmentType === AssessmentType.TRL;
  const scoreType = ASSESSMENT_TYPE_SCORE_TYPE[survey.assessmentType];
  const typeLabel = ASSESSMENT_TYPE_TITLES[survey.assessmentType];
  const assessmentTitle = isMilitaryTrl ? 'Military TRL' : typeLabel;

  const buildSubtitle = (): React.ReactNode => {
    if (!isTrlBased) {
      return <span className="text-xs font-light whitespace-nowrap text-[#1F1C1AB2]">Score</span>;
    }

    if (useDomainInfo.length === 0) {
      return <span className="text-xs font-light whitespace-nowrap text-[#1F1C1AB2]">Score</span>;
    }

    const additionalDomainsInOtherUses = useDomainInfo.slice(1).reduce((acc, info) => {
      return acc + info.domainNames.length;
    }, 0);
    const additionalUses = useDomainInfo.length - 1;
    const firstUseInfo = useDomainInfo[0];
    const firstDomainName = firstUseInfo.domainNames[0] ?? '';
    const firstUseName = firstUseInfo.useName;
    const additionalDomains = firstUseInfo.domainNames.length - 1;
    const additionalCount = additionalDomains + additionalUses + additionalDomainsInOtherUses;

    return (
      <div className="flex flex-row flex-wrap gap-1 text-xs mt-1 font-light text-[#1F1C1AB2]">
        <span>{firstUseName}</span>
        {firstDomainName && (
          <>
            <span>•</span>
            <span>{firstDomainName}</span>
          </>
        )}
        {additionalCount > 0 && <span className="text-gray-400">+{additionalCount}</span>}
      </div>
    );
  };

  return (
    <>
      <Tile hoverReveal>
        <div className="flex flex-col gap-3">
          <div className="flex justify-between gap-2">
            <div className="min-w-0 flex-1 space-y-2">
              {title && <div className="text-base font-medium text-gray-600">{title}</div>}
              <Score
                score={null}
                size="medium"
                subtitle={buildSubtitle()}
                title={
                  <div className="flex items-center gap-2">
                    <BlueLabel text={assessmentTitle} />
                    <Badge
                      size="xs"
                      variant="primary"
                    >
                      Draft
                    </Badge>
                  </div>
                }
                type={scoreType}
              />
            </div>

            <DropdownMenuComponent
              items={[
                {
                  onClick: handleDelete,
                  text: 'Discard',
                  variant: 'danger',
                },
              ]}
              trigger={
                <IconButton.HoverReveal
                  ariaLabel="Open menu"
                  icon={<EllipsisVertical />}
                  variant="secondary"
                />
              }
            />
          </div>

          <div className="flex w-full flex-col gap-2">
            <ProgressBar progress={survey.progress ?? 0} />
            <Button
              className="w-full"
              onClick={handleNavigateToSurvey}
              variant="primary"
            >
              Resume
            </Button>
          </div>
        </div>

        <ConfirmDeleteModal
          isOpen={isModalOpen}
          itemName="survey"
          onConfirm={handleConfirmDelete}
          setIsOpen={setIsModalOpen}
        />
      </Tile>
    </>
  );
};
